import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './utils/AuthContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import RequestInvite from './pages/RequestInvite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import FlyerLibrary from './pages/FlyerLibrary';
import FlyerDetail from './pages/FlyerDetail';
import Branding from './pages/Branding';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminInviteCodes from './pages/AdminInviteCodes';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminSettings from './pages/AdminSettings';
import AdminTeam from './pages/AdminTeam';
import AdminFlyerImports from './pages/AdminFlyerImports';
import PublicInformation from './pages/PublicInformation';

// Components
import Layout from './components/Layout';
import SupportAccessBanner from './components/SupportAccessBanner';

const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false, brandingRequired = false }) => {
  const { isAuthenticated, isAdmin, isSuperAdmin, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (brandingRequired && !isAdmin && user?.branding?.onboardingComplete !== true) {
    return <Navigate to="/branding?onboarding=1" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (isAdmin) return <Navigate to="/admin" replace />;
    return <Navigate to={user?.branding?.onboardingComplete === true ? '/dashboard' : '/branding?onboarding=1'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/request-invite" element={<PublicRoute><RequestInvite /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/privacy" element={<PublicInformation />} />
      <Route path="/terms" element={<PublicInformation />} />
      <Route path="/data-deletion" element={<PublicInformation />} />
      <Route path="/support" element={<PublicInformation />} />
      
      <Route path="/dashboard" element={<ProtectedRoute brandingRequired><Dashboard /></ProtectedRoute>} />
      <Route path="/flyers" element={<ProtectedRoute brandingRequired><FlyerLibrary /></ProtectedRoute>} />
      <Route path="/flyers/:id" element={<ProtectedRoute brandingRequired><FlyerDetail /></ProtectedRoute>} />
      <Route path="/branding" element={<ProtectedRoute><Branding /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Branding /></ProtectedRoute>} />
      
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/invite-codes" element={<ProtectedRoute adminOnly><AdminInviteCodes /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/flyers" element={<ProtectedRoute adminOnly><FlyerLibrary /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/team" element={<ProtectedRoute adminOnly superAdminOnly><AdminTeam /></ProtectedRoute>} />
      <Route path="/admin/flyer-imports" element={<ProtectedRoute adminOnly superAdminOnly><AdminFlyerImports /></ProtectedRoute>} />
      
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SupportAccessBanner />
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#333',
              color: '#fff',
              borderRadius: '12px',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
